import { Center, Stack } from '@mantine/core'
import { z } from 'zod'
import { IconTargetArrow } from '@tabler/icons-react'
import { Typography } from '../../../../common/typography'
import { Metric, useMetrics, useMetricsData } from '../../../../widgets/api'
import { Fmt, FmtColored } from '../../../../widgets/format'
import { DashboardWidget } from './types'
import { useResolvedContext } from './useResolvedContext'
import {
  ColorSchemaFields,
  ComponentStatisticFields,
  ContextFields,
  ContextSchemaFields,
  MetricSelect,
  NeutralColorsToggle,
  TitleInput,
} from './OptionsFormFields'
import { WidgetEmpty, WidgetSuspense } from './WidgetShell'

const Schema = z.object({
  ...ContextSchemaFields,
  ...ColorSchemaFields,
  title: z.string().default(''),
  metric_key: z.string().default(''),
  component: z.string().default('percent'),
  statistic: z.string().default('value'),
})

type Settings = z.infer<typeof Schema>

function Inner({ settings }: { settings: Settings }) {
  const { data: allMetrics } = useMetrics()
  const metric = allMetrics.find((m) => m.key === settings.metric_key)
  if (!metric) return <WidgetEmpty message="Pick a metric" />
  return <InnerWithMetric metric={metric} settings={settings} />
}

function InnerWithMetric({
  metric,
  settings,
}: {
  metric: Metric
  settings: Settings
}) {
  const ctx = useResolvedContext(settings)
  const { data: dataPoints } = useMetricsData({
    period_type: ctx.period_type,
    period_range: false,
    periods: [ctx.period],
    subject_type: ctx.subject_type,
    subject_ids: [ctx.subject_id],
    metrics: [metric.key],
    components: [settings.component],
    statistics: [settings.statistic],
    competition_id: ctx.competition_id,
  })

  const value = dataPoints[0]?.value ?? null
  const title = settings.title || metric.short_name || metric.name
  const ValueText = settings.use_neutral_colors ? Fmt : FmtColored

  return (
    <Center h="100%" w="100%">
      <Stack gap="xs" align="center">
        <Typography
          fs="xl"
          fw={500}
          fc={{ light: 'gray-3', dark: 'gray-3' }}
          ta="center"
        >
          {title}
        </Typography>
        <div
          style={{
            color: 'white',
            fontSize: 64,
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          <ValueText
            metric={metric.key}
            component={settings.component}
            statistic={settings.statistic}
            value={value}
          />
        </div>
      </Stack>
    </Center>
  )
}

function Component({ settings }: { settings: Settings }) {
  return (
    <div
      style={{
        height: '100%',
        width: '100%',
        background: 'var(--mantine-color-gray-9)',
        borderRadius: 'var(--mantine-radius-sm)',
        padding: 'var(--mantine-spacing-md)',
        boxSizing: 'border-box',
      }}
    >
      <WidgetSuspense>
        <Inner settings={settings} />
      </WidgetSuspense>
    </div>
  )
}

function OptionsForm({
  value,
  onChange,
}: {
  value: Settings
  onChange: (next: Settings) => void
}) {
  const patch = (p: Partial<Settings>) => onChange({ ...value, ...p })
  return (
    <Stack gap="xs">
      <TitleInput value={value.title} onChange={(v) => patch({ title: v })} />
      <MetricSelect
        value={value.metric_key}
        onChange={(v) => patch({ metric_key: v })}
      />
      <ComponentStatisticFields
        component={value.component}
        statistic={value.statistic}
        compatibility={KpiTileWidget.compatibility}
        onChange={(p) => patch(p as Partial<Settings>)}
      />
      <ContextFields
        subject_type={value.subject_type}
        subject_id={value.subject_id}
        period_type={value.period_type}
        period={value.period}
        use_max_period={value.use_max_period}
        onChange={(p) => patch(p as Partial<Settings>)}
      />
      <NeutralColorsToggle
        value={value.use_neutral_colors}
        onChange={(v) => patch({ use_neutral_colors: v })}
      />
    </Stack>
  )
}

export const KpiTileWidget: DashboardWidget<Settings> = {
  type: 'kpi_tile',
  label: 'KPI Tile',
  description: 'Big-number tile for a single metric',
  icon: IconTargetArrow,
  minW: 1,
  minH: 1,
  maxW: 1,
  maxH: 1,
  schema: Schema,
  defaultSettings: Schema.parse({}),
  compatibility: {
    actual: ['value', 'value_ytd', 'rank', 'team_rank'],
    target: ['value', 'value_ytd'],
    percent: ['value', 'value_ytd'],
    score: ['value', 'value_ytd', 'rank', 'team_rank'],
  },
  Component,
  OptionsForm,
}
